import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Popover,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { styled, alpha, useTheme, type Theme } from '@mui/material/styles';
import { useAppSelector, useAppDispatch } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FilterListIcon from '@mui/icons-material/FilterList';
import HexagonOutlinedIcon from '@mui/icons-material/HexagonOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { AddBoundaryModal } from './AddBoundaryModal';
import { EditBoundaryModal } from './EditBoundaryModal';
import { ImportBoundaryModal } from './ImportBoundaryModal';
import CustomStyledDataGrid from 'components/MuiDataGrid/CustomStyledDataGrid';
import { type GridColDef } from '@mui/x-data-grid';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export interface BoundaryRecord {
  id: string;
  gradeName: string;
  gradeId?: string | null;
  locationName: string;
  locationId?: string | null;
  pos: number;
  easting: number;
  northing: number;
  elevation: number;
  createdAt?: string;
}

const Ring = styled(Box, {
  shouldForwardProp: (p) => p !== 'delay' && p !== 'sz',
})<{ delay: number; sz: number }>(({ theme, delay, sz }) => ({
  position: 'absolute',
  width: sz,
  height: sz,
  borderRadius: '50%',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
  '@keyframes radar': {
    '0%': { transform: 'scale(0.8)', opacity: 0.5 },
    '80%': { opacity: 0.08 },
    '100%': { transform: 'scale(2.6)', opacity: 0 },
  },
  animation: 'radar 3s ease-out infinite',
  animationDelay: `${delay}s`,
  pointerEvents: 'none',
}));

const fmtCoord = ({ value }: { value: unknown }) => {
  const n = Number(value);
  return !isNaN(n) ? n.toFixed(2) : '—';
};

const API_BASE = `${process.env.REACT_APP_MANAGEMENT_API_URL ?? ''}/api`;

interface FailedBoundary extends Record<string, unknown> {
  locationName: string;
  reason: string;
}

interface BulkCreateResult {
  created: number;
  failed: number;
  failedItems: FailedBoundary[];
}

const BoundaryImporterContent = (): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { accessToken, customer } = useAppSelector((s) => s.authReducer);
  const customerId = customer.id;

  const [boundaries, setBoundaries] = useState<BoundaryRecord[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editBoundary, setEditBoundary] = useState<BoundaryRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState<{
    created: number;
    failedItems: FailedBoundary[];
  } | null>(null);
  const [colAnchor, setColAnchor] = useState<HTMLElement | null>(null);
  const [colVisibility, setColVisibility] = useState<Record<string, boolean>>({
    gradeName: false,
    locationName: true,
    pos: true,
    easting: true,
    northing: true,
    elevation: true,
    createdAt: false,
  });

  const fetchBoundaries = useCallback(async () => {
    if (!accessToken || !customerId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/boundary-importer?customerId=${customerId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (!res.ok) throw new Error(`Failed to fetch boundaries: ${res.status}`);
      const data: Array<Record<string, unknown>> = await res.json();
      const mapped = data.map((r) => {
        const grade = r.grade as Record<string, unknown> | null | undefined;
        const location = r.location as
          | Record<string, unknown>
          | null
          | undefined;
        return {
          id: r.boundaryId as string,
          gradeName: (grade?.name as string) ?? '',
          locationName: grade
            ? (grade.locationName as string) ?? ''
            : (location?.name as string) ?? '',
          pos: r.pos as number,
          easting: r.easting as number,
          northing: r.northing as number,
          elevation: r.elevation as number,
          createdAt: (r.createdAt as string) ?? '',
        };
      });
      mapped.sort((a, b) => {
        if (!a.createdAt && !b.createdAt) return 0;
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt.localeCompare(a.createdAt);
      });
      setBoundaries(mapped);
    } catch (e) {
      console.error(e);
      dispatch(
        showNotification({
          message: t('boundary_importer_fetch_failed'),
          type: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken, customerId, dispatch, t]);

  useEffect(() => {
    void fetchBoundaries();
  }, [fetchBoundaries]);

  const toast = (message: string, type: 'success' | 'error') => {
    dispatch(showNotification({ message, type }));
  };

  const failedRowCount = (failedItems: FailedBoundary[]): number =>
    failedItems.reduce((sum, item) => {
      const pts = item.boundaries as unknown[] | undefined;
      return sum + (pts && pts.length > 0 ? pts.length : 1);
    }, 0);

  const downloadFailedItems = (failedItems: FailedBoundary[]) => {
    const rows = failedItems.flatMap((item) => {
      const pts = item.boundaries as
        | Array<{
            pos: number;
            easting: number;
            northing: number;
            elevation: number;
          }>
        | undefined;
      if (!pts || pts.length === 0) {
        return [
          {
            locationName: item.locationName,
            pos: '',
            easting: '',
            northing: '',
            elevation: '',
            reason: item.reason,
          },
        ];
      }
      return pts.map((b) => ({
        locationName: item.locationName,
        pos: String(b.pos),
        easting: String(b.easting),
        northing: String(b.northing),
        elevation: String(b.elevation),
        reason: item.reason,
      }));
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Failed Boundaries');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([buf], { type: 'application/octet-stream' }),
      `failed_boundaries_${Date.now()}.xlsx`
    );
  };

  const handleAdd = async (records: BoundaryRecord[]) => {
    try {
      const grouped: Record<
        string,
        {
          meta: {
            gradeId: string | null;
            locationId: string | null;
            locationName: string;
          };
          boundaries: Array<{
            pos: number;
            easting: number;
            northing: number;
            elevation: number;
          }>;
        }
      > = {};
      for (const r of records) {
        const key = r.gradeId ?? r.locationId ?? r.locationName;
        if (!grouped[key]) {
          grouped[key] = {
            meta: {
              gradeId: r.gradeId ?? null,
              locationId: r.locationId ?? null,
              locationName: r.locationName,
            },
            boundaries: [],
          };
        }
        grouped[key].boundaries.push({
          pos: r.pos,
          easting: r.easting,
          northing: r.northing,
          elevation: r.elevation,
        });
      }
      const items = Object.values(grouped).map(({ meta, boundaries }) => ({
        ...(meta.gradeId != null
          ? { gradeId: meta.gradeId }
          : { locationId: meta.locationId }),
        locationName: meta.locationName,
        boundaries,
      }));

      const res = await fetch(`${API_BASE}/boundary-importer/bulk-create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customerId, items }),
      });
      if (!res.ok) {
        let msg = t('boundary_importer_toast_add_failed');
        try {
          const body: unknown = await res.json();
          if (
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof (body as Record<string, unknown>).message === 'string'
          ) {
            msg = (body as Record<string, string>).message;
          }
        } catch {}
        throw new Error(msg);
      }
      await fetchBoundaries();
      setAddOpen(false);
      toast(t('boundary_importer_toast_added'), 'success');
    } catch (e) {
      toast(
        e instanceof Error
          ? e.message
          : t('boundary_importer_toast_add_failed'),
        'error'
      );
    }
  };

  const handleImport = async (rows: BoundaryRecord[]) => {
    try {
      // Group rows by locationName
      const grouped: Record<
        string,
        Array<{
          pos: number;
          easting: number;
          northing: number;
          elevation: number;
        }>
      > = {};
      for (const row of rows) {
        if (!grouped[row.locationName]) grouped[row.locationName] = [];
        grouped[row.locationName].push({
          pos: row.pos,
          easting: row.easting,
          northing: row.northing,
          elevation: row.elevation,
        });
      }

      const items = Object.entries(grouped).map(
        ([locationName, boundaries]) => ({
          locationName,
          boundaries,
        })
      );

      const res = await fetch(`${API_BASE}/boundary-importer/bulk-create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customerId, items }),
      });
      if (!res.ok) throw new Error(`Import failed: ${res.status}`);

      const result = (await res.json()) as BulkCreateResult;
      await fetchBoundaries();
      setImportOpen(false);

      if (result.failedItems && result.failedItems.length > 0) {
        setImportResult({
          created: result.created,
          failedItems: result.failedItems,
        });
        toast(
          t('boundary_importer_toast_partial', {
            created: result.created,
            failed: failedRowCount(result.failedItems),
          }),
          result.created > 0 ? 'success' : 'error'
        );
      } else {
        toast(
          t('boundary_importer_toast_imported', { count: result.created }),
          'success'
        );
      }
    } catch (e) {
      toast(
        e instanceof Error
          ? e.message
          : t('boundary_importer_toast_import_failed'),
        'error'
      );
    }
  };

  const handleUpdate = async (updated: Omit<BoundaryRecord, 'id'>) => {
    if (!editBoundary) return;
    try {
      const res = await fetch(
        `${API_BASE}/boundary-importer/${editBoundary.id}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            pos: updated.pos,
            easting: updated.easting,
            northing: updated.northing,
            elevation: updated.elevation,
          }),
        }
      );
      if (!res.ok) {
        let msg = `Update failed: ${res.status}`;
        try {
          const body: unknown = await res.json();
          if (
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof (body as Record<string, unknown>).message === 'string'
          ) {
            msg = (body as Record<string, string>).message;
          }
        } catch {}
        throw new Error(msg);
      }
      await fetchBoundaries();
      setEditBoundary(null);
      toast(t('boundary_importer_toast_updated'), 'success');
    } catch (e) {
      toast(
        e instanceof Error
          ? e.message
          : t('boundary_importer_toast_update_failed'),
        'error'
      );
      throw e;
    }
  };

  const handleDelete = useCallback((id: string, name: string) => {
    setDeleteTarget({ id, name });
    setDeleteConfirmText('');
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(
        `${API_BASE}/boundary-importer/${deleteTarget.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
      await fetchBoundaries();
      toast(
        t('boundary_importer_toast_deleted', { name: deleteTarget.name }),
        'success'
      );
    } catch (e) {
      toast(
        e instanceof Error
          ? e.message
          : t('boundary_importer_toast_delete_failed'),
        'error'
      );
    } finally {
      setDeleteTarget(null);
      setDeleteConfirmText('');
    }
  };

  const hasData = boundaries.length > 0;

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'gradeName',
        headerName: t('boundary_importer_col_grade_name'),
        flex: 1.5,
        minWidth: 140,
        valueFormatter: ({ value }: { value: unknown }) =>
          value ? String(value) : '—',
      },
      {
        field: 'locationName',
        headerName: t('boundary_importer_col_location_name'),
        flex: 1.5,
        minWidth: 140,
        valueFormatter: ({ value }: { value: unknown }) =>
          value ? String(value) : '—',
      },
      {
        field: 'pos',
        headerName: t('boundary_importer_col_pos'),
        width: 80,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        valueFormatter: ({ value }: { value: unknown }) => String(value),
      },
      {
        field: 'easting',
        headerName: t('boundary_importer_col_easting'),
        flex: 1,
        minWidth: 110,
        type: 'number',
        valueFormatter: fmtCoord,
      },
      {
        field: 'northing',
        headerName: t('boundary_importer_col_northing'),
        flex: 1,
        minWidth: 110,
        type: 'number',
        valueFormatter: fmtCoord,
      },
      {
        field: 'elevation',
        headerName: t('boundary_importer_col_elevation'),
        flex: 1,
        minWidth: 110,
        type: 'number',
        valueFormatter: fmtCoord,
      },
      {
        field: 'createdAt',
        headerName: t('boundary_importer_col_created_at'),
        flex: 1.2,
        minWidth: 160,
        valueFormatter: ({ value }: { value: unknown }) => {
          if (!value) return '—';
          const d = new Date(value as string);
          return isNaN(d.getTime()) ? String(value) : d.toLocaleString();
        },
      },
      {
        field: 'actions',
        headerName: t('boundary_importer_col_actions'),
        width: 100,
        sortable: false,
        disableColumnMenu: true,
        disableReorder: true,
        renderCell: (params) => (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Tooltip title={t('boundary_importer_edit')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditBoundary(params.row as BoundaryRecord);
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('boundary_importer_delete')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  const row = params.row as BoundaryRecord;
                  handleDelete(row.id, `${row.locationName} #${row.pos}`);
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [handleDelete, t]
  );

  const palette = clientThemePalette[CLIENT_THEME].light;

  return (
    <Box
      sx={{
        px: '32px',
        pt: '20px',
        pb: '20px',
        mx: -4,
        height: 'calc(100vh - 130px)',
        minHeight: 500,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* ── HEADER ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        {/* Left: title + subtitle + stat pills */}
        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
            color="text.primary"
            lineHeight={1.2}
          >
            {t('boundary_importer_title')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.4, mb: 1.5 }}
          >
            {t('boundary_importer_subtitle')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
                px: 1.4,
                py: 0.35,
                borderRadius: '6px',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.primary.main, 0.22),
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                }}
              />
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                {boundaries.length} {t('boundary_importer_total')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right: action buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            flexShrink: 0,
            mt: 0.5,
            alignItems: 'center',
          }}
        >
          <Tooltip title={t('boundary_importer_btn_refresh')}>
            <span>
              <IconButton
                onClick={() => {
                  void fetchBoundaries();
                }}
                disabled={loading}
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: (t) => alpha(t.palette.divider, 0.6),
                  borderRadius: '8px',
                  p: 0.8,
                }}
              >
                <RefreshIcon
                  fontSize="small"
                  sx={{
                    transition: 'transform 0.5s',
                    transform: loading ? 'rotate(360deg)' : 'none',
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setAddOpen(true);
            }}
            sx={{ px: 2.5, fontWeight: 600, borderRadius: '8px' }}
          >
            {t('boundary_importer_btn_add')}
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => {
              setImportOpen(true);
            }}
            sx={{ px: 2.5, fontWeight: 600, borderRadius: '8px' }}
          >
            {t('boundary_importer_btn_import')}
          </Button>
        </Box>
      </Box>

      {/* ── BODY ── */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading && boundaries.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : !hasData ? (
          /* ── Empty state ── */
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              backgroundImage: `radial-gradient(circle, ${alpha(
                theme.palette.primary.main,
                0.1
              )} 1px, transparent 1px)`,
              backgroundSize: '28px 28px',
              borderRadius: 3,
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.divider, 0.5),
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: (t) =>
                  `radial-gradient(ellipse 65% 65% at 50% 50%, transparent 25%, ${t.palette.background.default} 100%)`,
                pointerEvents: 'none',
              }}
            />

            <Box
              sx={{
                position: 'relative',
                width: 96,
                height: 96,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3.5,
                zIndex: 1,
              }}
            >
              <Ring delay={0} sz={96} />
              <Ring delay={1} sz={96} />
              <Ring delay={2} sz={96} />
              <Box
                sx={{
                  width: 78,
                  height: 78,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                  border: '2px solid',
                  borderColor: (t) => alpha(t.palette.primary.main, 0.35),
                  boxShadow: `0 0 40px ${alpha(
                    theme.palette.primary.main,
                    0.25
                  )}`,
                  zIndex: 1,
                  position: 'relative',
                }}
              >
                <HexagonOutlinedIcon
                  sx={{ fontSize: 40, color: 'primary.main' }}
                />
              </Box>
            </Box>

            <Typography
              variant="h5"
              fontWeight={700}
              color="text.primary"
              textAlign="center"
              sx={{ zIndex: 1 }}
            >
              {t('boundary_importer_empty_title')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: 1, mb: 4, maxWidth: 360, lineHeight: 1.8, zIndex: 1 }}
            >
              {t('boundary_importer_empty_body')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, zIndex: 1 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setAddOpen(true);
                }}
                sx={{ px: 3.5, py: 1.1, fontWeight: 600, borderRadius: '8px' }}
              >
                {t('boundary_importer_btn_add')}
              </Button>
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => {
                  setImportOpen(true);
                }}
                sx={{ px: 3.5, py: 1.1, fontWeight: 600, borderRadius: '8px' }}
              >
                {t('boundary_importer_btn_import')}
              </Button>
            </Box>
          </Box>
        ) : (
          /* ── Data table ── */
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
              >
                {t('boundary_importer_all_boundaries')}{' '}
                <Typography
                  component="span"
                  variant="body2"
                  color="primary.main"
                  fontWeight={700}
                >
                  ({boundaries.length})
                </Typography>
              </Typography>
              <Button
                size="small"
                startIcon={<FilterListIcon sx={{ fontSize: 15 }} />}
                onClick={(e) => {
                  setColAnchor(e.currentTarget);
                }}
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.76rem',
                  borderRadius: '6px',
                }}
              >
                {t('boundary_importer_filters')}
              </Button>
              <Popover
                open={Boolean(colAnchor)}
                anchorEl={colAnchor}
                onClose={() => {
                  setColAnchor(null);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    mt: 0.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: (th: Theme) => alpha(th.palette.divider, 0.5),
                    p: 1.5,
                    minWidth: 200,
                  },
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    px: 0.5,
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  {t('boundary_importer_col_visibility')}
                </Typography>
                {Object.keys(colVisibility).map((col) => (
                  <Box key={col} sx={{ display: 'block' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={colVisibility[col]}
                          onChange={(e) => {
                            setColVisibility((prev) => ({
                              ...prev,
                              [col]: e.target.checked,
                            }));
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ fontSize: '0.82rem' }}
                        >
                          {t(
                            `boundary_importer_col_${
                              col === 'gradeName'
                                ? 'grade_name'
                                : col === 'locationName'
                                ? 'location_name'
                                : col === 'createdAt'
                                ? 'created_at'
                                : col
                            }`
                          )}
                        </Typography>
                      }
                      sx={{ mx: 0, width: '100%' }}
                    />
                  </Box>
                ))}
              </Popover>
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: (t) => alpha(t.palette.background.paper, 0.6),
                    zIndex: 10,
                    borderRadius: 2,
                  }}
                >
                  <CircularProgress size={32} />
                </Box>
              )}
              <CustomStyledDataGrid
                rows={boundaries}
                columns={columns}
                getRowId={(row) => row.id}
                disableRowSelectionOnClick
                columnVisibilityModel={colVisibility}
              />
            </Box>
          </Box>
        )}
      </Box>

      <AddBoundaryModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        onAdd={(boundary) => {
          void handleAdd(boundary);
        }}
      />
      <EditBoundaryModal
        open={editBoundary !== null}
        boundary={editBoundary}
        onClose={() => {
          setEditBoundary(null);
        }}
        onSave={async (updated) => {
          await handleUpdate(updated);
        }}
      />
      <ImportBoundaryModal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
        }}
        onImport={async (rows) => {
          await handleImport(rows);
        }}
      />

      {/* ── Import Result Dialog ── */}
      <Dialog
        open={importResult !== null}
        onClose={() => {
          setImportResult(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (th: Theme) => alpha(th.palette.warning.main, 0.3),
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (th: Theme) => alpha(th.palette.warning.main, 0.12),
                border: '1px solid',
                borderColor: (th: Theme) => alpha(th.palette.warning.main, 0.3),
                flexShrink: 0,
              }}
            >
              <HexagonOutlinedIcon
                sx={{ fontSize: 18, color: 'warning.main' }}
              />
            </Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {t('boundary_importer_import_result_title')}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 1.5, pb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 2,
                textAlign: 'center',
                bgcolor: (th: Theme) => alpha(th.palette.success.main, 0.08),
                border: '1px solid',
                borderColor: (th: Theme) => alpha(th.palette.success.main, 0.2),
              }}
            >
              <Typography variant="h5" fontWeight={700} color="success.main">
                {importResult?.created ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('boundary_importer_result_success')}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 2,
                textAlign: 'center',
                bgcolor: (th: Theme) => alpha(th.palette.error.main, 0.08),
                border: '1px solid',
                borderColor: (th: Theme) => alpha(th.palette.error.main, 0.2),
              }}
            >
              <Typography variant="h5" fontWeight={700} color="error.main">
                {importResult ? failedRowCount(importResult.failedItems) : 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('boundary_importer_result_failed')}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('boundary_importer_result_body')}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            borderTop: '1px solid',
            borderColor: (th: Theme) => alpha(th.palette.divider, 0.5),
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setImportResult(null);
            }}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('boundary_importer_cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              if (importResult) downloadFailedItems(importResult.failedItems);
            }}
            sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
          >
            {t('boundary_importer_result_download')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteConfirmText('');
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (th: Theme) => alpha(th.palette.error.main, 0.25),
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (th: Theme) => alpha(th.palette.error.main, 0.1),
                border: '1px solid',
                borderColor: (th: Theme) => alpha(th.palette.error.main, 0.25),
                flexShrink: 0,
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                {t('boundary_importer_delete_confirm_title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('boundary_importer_delete_confirm_subtitle')}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 1.5, pb: 2 }}>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              mb: 2,
              borderRadius: 2,
              bgcolor: (th: Theme) => alpha(th.palette.error.main, 0.06),
              border: '1px solid',
              borderColor: (th: Theme) => alpha(th.palette.error.main, 0.2),
            }}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              color="error.main"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              {deleteTarget?.name}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('boundary_importer_delete_confirm_instruction')}
          </Typography>

          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder={deleteTarget?.name ?? ''}
            value={deleteConfirmText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDeleteConfirmText(e.target.value);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (
                e.key === 'Enter' &&
                deleteConfirmText === deleteTarget?.name
              ) {
                void confirmDelete();
              }
            }}
            error={
              deleteConfirmText.length > 0 &&
              deleteConfirmText !== deleteTarget?.name
            }
            sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'monospace' } }}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            borderTop: '1px solid',
            borderColor: (th: Theme) => alpha(th.palette.divider, 0.5),
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setDeleteTarget(null);
              setDeleteConfirmText('');
            }}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('boundary_importer_cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteConfirmText !== deleteTarget?.name}
            onClick={() => {
              void confirmDelete();
            }}
            sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
          >
            {t('boundary_importer_delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const BoundaryImporterView = (): JSX.Element => {
  return (
    <CustomMuiThemeProvider>
      <BoundaryImporterContent />
    </CustomMuiThemeProvider>
  );
};
